/**
 * Smart Product Finder - Core UI/UX Script (Optimized)
 * 
 * This optimized script handles all user interface interactions, DOM manipulation,
 * and visual elements for the Smart Product Finder. It delegates all filtering 
 * decisions to the Smart Filter Brain for intelligent behavior.
 * 
 * Key Optimizations:
 * - Removed duplicate navigation step definitions (delegated to brain)
 * - Streamlined DOM element caching
 * - Optimized event handler management
 * - Enhanced error handling and fallback mechanisms
 * - Improved memory management with proper cleanup
 * 
 * Responsibilities:
 * - UI/UX management and animations
 * - DOM element creation and manipulation  
 * - Event handling (clicks, keyboard, scroll)
 * - Data loading and caching
 * - State management and breadcrumb display
 * - Fallback UI when brain is unavailable
 */

class SmartProductFinderUI {
  constructor(config = {}) {
    // Handle both old-style parameters and new config object
    if (typeof config === 'string') {
      // Old-style: constructor(sectionId, sectionIdRaw, settings)
      this.sectionId = config;
      this.sectionIdRaw = arguments[1] || config;
      this.settings = arguments[2] || {};
    } else {
      // New-style: constructor(config)
      this.sectionId = config.sectionId?.replace(/_/g, '-') || config.sectionId;
      this.sectionIdRaw = config.sectionIdRaw || config.sectionId;
      this.settings = {
        remoteDataUrl: config.remoteDataUrl || 'https://www.topwebworks.com/assets/test/aop/product-data.json',
        cardInfoFontSize: config.cardInfoFontSize || 12,
        ...config
      };
    }
      // State management
    this.allProducts = [];
    this.currentFilters = {};
    this.currentStep = 0;    this.smartFilterBrain = null;
    this.resizeObserver = null;
    this.modalManuallyClosedRecently = false;
    this.modalClosedForFilterState = null; // Track the exact filter state when modal was closed
    
    // DOM elements - will be cached on init
    this.elements = {};
    
    // Event handlers for cleanup
    this.eventHandlers = {
      scroll: null,
      resize: null,
      horizontalScroll: null,
      windowScroll: null,
      toggleDropdown: null,
      handleDropdownKeydown: null,
      closeDropdownOutside: null
    };
    
    // Initialize the system
    this.init();
  }
  /**
   * Initialize the entire Smart Product Finder system
   */
  async init() {
    try {
      // Load the brain first
      await this.initializeBrain();
      
      // Cache DOM elements
      this.cacheElements();
      
      // Validate critical elements
      if (!this.validateElements()) {
        console.warn('Smart Product Finder: Required DOM elements not found');
        return;
      }
      
      // Load product data
      const dataLoaded = await this.loadData();
      
      // Setup UI components
      this.setupScrollControls();
      this.initNavigation();
      this.setupGlobalFunctions();
      
      // Start the filtering process if data loaded
      if (dataLoaded) {
        this.updateInterface();
      }
      
    } catch (error) {
      console.error('Smart Product Finder initialization failed:', error);
    }
  }
  /**
   * Simple logging for UI debug messages
   */
  log(message, data = null) {
    if (this.settings.debugMode) {
      console.log(`🔄 UI: ${message}`, data || '');
    }
  }
  /**
   * Load and initialize the Smart Filter Brain
   */
  async initializeBrain() {
    try {
      const BrainClass = await this.loadSmartBrain();
      if (BrainClass && typeof BrainClass === 'function') {
        this.smartFilterBrain = new BrainClass();
        // Pass debug mode setting to brain
        if (this.smartFilterBrain && this.smartFilterBrain.config) {
          this.smartFilterBrain.config.debugMode = this.settings.debugMode || false;
        }
        this.log('Smart Filter Brain loaded successfully');
      } else if (BrainClass && typeof BrainClass === 'object') {
        // Brain already instantiated
        this.smartFilterBrain = BrainClass;
        // Pass debug mode setting to brain
        if (this.smartFilterBrain && this.smartFilterBrain.config) {
          this.smartFilterBrain.config.debugMode = this.settings.debugMode || false;
        }
        this.log('Smart Filter Brain instance loaded successfully');
      } else {
        console.warn('Smart Filter Brain failed to load, using fallback logic');
      }
    } catch (error) {
      console.error('Brain initialization error:', error);
    }
  }

  /**
   * Load the Smart Filter Brain script
   */
  loadSmartBrain() {
    return new Promise((resolve, reject) => {
      if (window.SmartProductFinderBrain) {
        resolve(window.SmartProductFinderBrain);
        return;
      }
      
      const brainScript = document.createElement('script');
      brainScript.src = this.settings.brainScriptUrl || '/assets/smart-product-finder-brain.js';
      
      brainScript.onload = () => {
        setTimeout(() => {
          resolve(window.SmartProductFinderBrain || null);
        }, 50);
      };
      
      brainScript.onerror = () => {
        console.warn('Smart Filter Brain script failed to load');
        resolve(null);
      };
      
      document.head.appendChild(brainScript);
    });
  }

  /**
   * Cache all DOM elements for performance
   */
  cacheElements() {
    const id = this.sectionIdRaw;
    
    this.elements = {
      section: document.getElementById(`smart-finder-${id}`),
      breadcrumbTrail: document.getElementById(`breadcrumb-${id}`),
      nextFilter: document.getElementById(`next-filter-${id}`),
      filterDropdownButton: document.getElementById(`filter-dropdown-button-${id}`),
      filterDropdownMenu: document.getElementById(`filter-dropdown-menu-${id}`),
      finalResults: document.getElementById(`final-results-${id}`),
      finalContent: document.getElementById(`final-content-${id}`),
      finderTitle: document.getElementById(`finder-title-${id}`),
      finderControls: null,
      scrollLeft: null,
      scrollRight: null
    };
    
    // Get nested elements
    if (this.elements.section) {
      this.elements.finderControls = this.elements.section.querySelector('.finder-controls');
      this.elements.scrollLeft = this.elements.section.querySelector('.scroll-left');
      this.elements.scrollRight = this.elements.section.querySelector('.scroll-right');
    }
  }

  /**
   * Validate that critical DOM elements exist
   */
  validateElements() {
    const required = ['nextFilter', 'filterDropdownButton', 'filterDropdownMenu'];
    return required.every(key => {
      const exists = !!this.elements[key];
      if (!exists) {
        console.error(`Smart Product Finder: Missing required element: ${key}`);
      }
      return exists;
    });
  }

  /**
   * Load product data from GraphQL or test data
   */
  async loadData() {
    try {
      const remoteUrl = this.settings.remoteDataUrl || 
        'https://www.topwebworks.com/assets/test/aop/product-data.json';
      
      const cacheKey = `smart-finder-data-${this.sectionId}`;
      
      // Check cache first
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const { timestamp, data } = JSON.parse(cachedData);
          if (Date.now() - timestamp < 1800000) { // 30 minute cache
            this.allProducts = data;
            return true;
          }
        } catch (e) {
          console.warn('Invalid cache data');
        }
      }
      
      // Fetch fresh data
      const response = await fetch(remoteUrl, {
        mode: 'cors',
        cache: 'default',
        credentials: 'omit',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const jsonData = await response.json();
        const processed = this.processGraphQLData(jsonData);
        
        // Cache the data
        if (processed) {
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({
              timestamp: Date.now(),
              data: this.allProducts
            }));
          } catch (e) {
            console.warn('Unable to cache data');
          }
        }
        
        return processed;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Data fetch error:', error);
      // Fallback to test data
      this.generateTestData();
      return true;
    }
  }
  /**
   * Process GraphQL data and transform to brain-compatible format
   */
  processGraphQLData(data) {
    try {
      // Handle GraphQL structure: data.data.products.edges[].node
      if (data && data.data && data.data.products && Array.isArray(data.data.products.edges)) {
        const rawProducts = data.data.products.edges.map(edge => edge.node);
        
        // Transform to brain-compatible format
        this.allProducts = rawProducts.map(product => {
          const transformed = {
            id: product.id,
            handle: product.handle,
            title: product.title,
            vendor: product.vendor,
            tags: product.tags || [],
            url: `/products/${product.handle}`
          };

          // Extract values from metafield objects and handle JSON strings
          if (product.subject?.value) {
            transformed['custom.subject'] = product.subject.value;
          }
          
          if (product.productType?.value) {
            transformed['custom.product_type'] = product.productType.value;
          }
          
          if (product.courseType?.value) {
            transformed['custom.course_type'] = product.courseType.value;
          }
          
          if (product.gradeLevel?.value) {
            try {
              // Parse JSON string if it looks like JSON
              const gradeValue = product.gradeLevel.value;
              if (typeof gradeValue === 'string' && gradeValue.startsWith('[') && gradeValue.endsWith(']')) {
                transformed['custom.grade_level'] = JSON.parse(gradeValue);
              } else {
                transformed['custom.grade_level'] = gradeValue;
              }
            } catch (e) {
              transformed['custom.grade_level'] = product.gradeLevel.value;
            }
          }
          
          if (product.learningStyle?.value) {
            try {
              // Parse JSON string if it looks like JSON
              const styleValue = product.learningStyle.value;
              if (typeof styleValue === 'string' && styleValue.startsWith('[') && styleValue.endsWith(']')) {
                transformed['custom.learning_style'] = JSON.parse(styleValue);
              } else {
                transformed['custom.learning_style'] = styleValue;
              }
            } catch (e) {
              transformed['custom.learning_style'] = product.learningStyle.value;
            }
          }

          return transformed;
        });
        
        this.log(`Smart Product Finder: Processed ${this.allProducts.length} products`);
        return true;
      }
      
      // Fallback for old format
      if (data && data.data && Array.isArray(data.data)) {
        this.allProducts = data.data;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error processing GraphQL data:', error);
      return false;
    }
  }

  /**
   * Generate test data for demonstration
   */
  generateTestData() {
    this.allProducts = [
      {
        title: 'Test Product 1',
        vendor: 'Test Vendor',
        url: '#',
        tags: ['Elementary School', 'Math'],
        custom: { subject: 'Mathematics', grade_level: '3rd Grade' }
      },
      {
        title: 'Test Product 2', 
        vendor: 'Test Vendor',
        url: '#',
        tags: ['Middle School', 'Science'],
        custom: { subject: 'Science', grade_level: '6th Grade' }
      }
    ];
  }

  /**
   * Setup horizontal scroll controls
   */
  setupScrollControls() {
    const { scrollLeft, scrollRight, finderControls } = this.elements;
    
    if (!scrollLeft || !scrollRight || !finderControls) return;

    // Scroll functionality
    scrollLeft.addEventListener('click', () => {
      finderControls.scrollBy({ left: -250, behavior: 'smooth' });
    });

    scrollRight.addEventListener('click', () => {
      finderControls.scrollBy({ left: 250, behavior: 'smooth' });
    });

    // Update scroll indicators
    const updateArrows = () => {
      // Hide left arrow if at beginning
      if (finderControls.scrollLeft <= 10) {
        scrollLeft.style.opacity = '0';
        scrollLeft.style.pointerEvents = 'none';
      } else {
        scrollLeft.style.opacity = '1';
        scrollLeft.style.pointerEvents = 'auto';
      }
      
      // Hide right arrow if at end
      const isAtEnd = Math.ceil(finderControls.scrollLeft + finderControls.clientWidth) >= 
                      finderControls.scrollWidth - 10;
      if (isAtEnd) {
        scrollRight.style.opacity = '0';
        scrollRight.style.pointerEvents = 'none';
      } else {
        scrollRight.style.opacity = '1';
        scrollRight.style.pointerEvents = 'auto';
      }
    };

    // Store handlers for cleanup
    this.eventHandlers.scroll = updateArrows;
    this.eventHandlers.resize = updateArrows;

    // Add ResizeObserver to detect layout changes
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        updateArrows();
      });
      this.resizeObserver.observe(finderControls);
    }

    updateArrows();
    finderControls.addEventListener('scroll', this.eventHandlers.scroll);
    window.addEventListener('resize', this.eventHandlers.resize);
  }
  /**
   * Initialize navigation and dropdown functionality with smart cache reset
   */
  initNavigation() {
    // Reset cache and state for page refresh scenarios
    if (this.smartFilterBrain && this.smartFilterBrain.resetForPageRefresh) {
      this.smartFilterBrain.resetForPageRefresh();
    }
    
    // Initialize filters and state
    this.currentFilters = {};
    this.currentStep = 0;
    
    this.setupDropdownEvents();
    this.setupWindowEvents();
  }
  /**
   * Setup dropdown event handlers
   */
  setupDropdownEvents() {
    const { filterDropdownButton, filterDropdownMenu, nextFilter, finderControls } = this.elements;
    
    if (!filterDropdownButton || !filterDropdownMenu || !nextFilter) return;

    // Remove existing listeners for cleanup
    this.cleanupDropdownEvents();

    // Create new handlers
    this.eventHandlers.toggleDropdown = (e) => {
      e.stopPropagation();
      nextFilter.classList.toggle('open');
      const isOpen = nextFilter.classList.contains('open');
      filterDropdownButton.setAttribute('aria-expanded', isOpen.toString());
      
      if (isOpen) {
        this.positionDropdown();
        // Focus first option
        const firstOption = filterDropdownMenu.querySelector('.filter-dropdown-option');
        if (firstOption) firstOption.focus();
      }
    };

    this.eventHandlers.handleDropdownKeydown = (e) => {
      this.handleKeyboardNavigation(e);
    };

    this.eventHandlers.closeDropdownOutside = (e) => {
      if (!nextFilter.contains(e.target)) {
        nextFilter.classList.remove('open');
        filterDropdownButton.setAttribute('aria-expanded', 'false');
      }
    };

    this.eventHandlers.closeDropdownOnHorizontalScroll = () => {
      if (nextFilter.classList.contains('open')) {
        nextFilter.classList.remove('open');
        filterDropdownButton.setAttribute('aria-expanded', 'false');
      }
    };

    this.eventHandlers.closeDropdownOnWindowScroll = () => {
      if (nextFilter.classList.contains('open')) {
        nextFilter.classList.remove('open');
        filterDropdownButton.setAttribute('aria-expanded', 'false');
      }
    };

    // Add event listeners
    filterDropdownButton.addEventListener('click', this.eventHandlers.toggleDropdown);
    filterDropdownButton.addEventListener('keydown', this.eventHandlers.handleDropdownKeydown);
    document.addEventListener('click', this.eventHandlers.closeDropdownOutside);
    
    if (finderControls) {
      finderControls.addEventListener('scroll', this.eventHandlers.closeDropdownOnHorizontalScroll);
    }
    window.addEventListener('scroll', this.eventHandlers.closeDropdownOnWindowScroll);
  }

  /**
   * Handle keyboard navigation for dropdown
   */
  handleKeyboardNavigation(e) {
    const { nextFilter, filterDropdownButton, filterDropdownMenu } = this.elements;
    const isOpen = nextFilter.classList.contains('open');
    
    switch(e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isOpen) {
          this.eventHandlers.toggleDropdown(e);
        }
        break;
      case 'Escape':
        if (isOpen) {
          nextFilter.classList.remove('open');
          filterDropdownButton.setAttribute('aria-expanded', 'false');
          filterDropdownButton.focus();
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          this.eventHandlers.toggleDropdown(e);
        } else {
          this.focusNextOption(1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          this.focusNextOption(-1);
        }
        break;
    }
  }

  /**
   * Focus next option in dropdown
   */
  focusNextOption(direction) {
    const { filterDropdownMenu } = this.elements;
    const options = filterDropdownMenu.querySelectorAll('.filter-dropdown-option');
    const focusedOption = document.activeElement;
    const currentIndex = Array.from(options).indexOf(focusedOption);
    
    let nextIndex;
    if (direction === 1) {
      nextIndex = (currentIndex + 1) % options.length;
    } else {
      nextIndex = currentIndex === -1 ? options.length - 1 : 
                  (currentIndex - 1 + options.length) % options.length;
    }
    
    options[nextIndex].focus();
  }

  /**
   * Position dropdown properly
   */
  positionDropdown() {
    const { filterDropdownButton, filterDropdownMenu } = this.elements;
    
    // Reset positioning
    filterDropdownMenu.style.transform = 'none';
    filterDropdownMenu.style.top = '';
    filterDropdownMenu.style.left = '';
    
    setTimeout(() => {
      const rect = filterDropdownButton.getBoundingClientRect();
      filterDropdownMenu.style.top = rect.bottom + 'px';
      filterDropdownMenu.style.left = rect.left + 'px';
      
      // Ensure it doesn't go off-screen
      const dropdownRect = filterDropdownMenu.getBoundingClientRect();
      const rightEdge = rect.left + dropdownRect.width;
      
      if (rightEdge > window.innerWidth - 10) {
        filterDropdownMenu.style.left = 
          Math.max(10, window.innerWidth - dropdownRect.width - 10) + 'px';
      }
    }, 10);
  }

  /**
   * Setup window event handlers
   */
  setupWindowEvents() {
    // Cleanup existing handlers
    this.cleanupWindowEvents();

    // Position dropdowns on scroll/resize
    this.eventHandlers.horizontalScroll = () => this.positionDropdown();
    this.eventHandlers.windowScroll = () => this.positionDropdown();

    if (this.elements.finderControls) {
      this.elements.finderControls.addEventListener('scroll', this.eventHandlers.horizontalScroll);
    }
    window.addEventListener('scroll', this.eventHandlers.windowScroll);
  }

  /**
   * Setup global functions for filter management  
   */
  setupGlobalFunctions() {
    // Remove filter function
    window[`removeFilter_${this.sectionId}`] = (filterKey) => {
      this.removeFilter(filterKey);
    };
    
    // Reset filters function
    window[`resetFilters_${this.sectionId}`] = () => {
      this.resetFilters();
    };
  }

  /**
   * Main interface update method
   */
  updateInterface() {
    this.updateBreadcrumbs();
    this.updateNextFilter();
    this.checkForResults();
    this.updateTitle();
  }
  /**
   * Update breadcrumb trail
   */
  updateBreadcrumbs() {
    const { breadcrumbTrail, finderControls } = this.elements;
    if (!breadcrumbTrail) return;

    // Store the previous scroll width before changes
    const prevScrollWidth = finderControls ? finderControls.scrollWidth : 0;

    breadcrumbTrail.innerHTML = '';

    const navSteps = this.getNavigationSteps();
      Object.keys(this.currentFilters).forEach(filterKey => {
      const step = navSteps.find(s => s.key === filterKey);
      if (!step) return;

      const item = document.createElement('div');
      item.className = 'breadcrumb-item';
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label', `Remove filter: ${step.label}: ${this.currentFilters[filterKey]}`);
      item.setAttribute('data-filter-key', filterKey);
      item.innerHTML = `
        <span class="breadcrumb-text">${step.label}: ${this.currentFilters[filterKey]}</span>
        <span class="breadcrumb-remove-icon">
          <span class="arrow-icon">❯</span>
        </span>
      `;

      // Add click event to entire breadcrumb item
      item.addEventListener('click', () => {
        this.removeFilter(filterKey);
      });

      // Add keyboard support
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.removeFilter(filterKey);
        }
      });

      breadcrumbTrail.appendChild(item);
    });
    
    // Update scroll arrows and auto-scroll after breadcrumb changes
    setTimeout(() => this.autoScrollToNewContent(prevScrollWidth), 10);
  }

  /**
   * Update next filter dropdown with brain delegation
   */
  updateNextFilter() {
    const { nextFilter, filterDropdownButton, filterDropdownMenu } = this.elements;
    
    if (!nextFilter || !filterDropdownButton || !filterDropdownMenu) {
      console.error('Smart Product Finder: Required DOM elements missing');
      return;
    }

    // Delegate to brain for intelligent decisions
    if (this.smartFilterBrain && this.smartFilterBrain.updateNextFilter) {
      return this.smartFilterBrain.updateNextFilter({
        nextFilter,
        filterDropdownButton,
        filterDropdownMenu,
        allProducts: this.allProducts,
        currentFilters: this.currentFilters,
        selectDropdownOption: (key, value) => this.selectDropdownOption(key, value)
      });
    }

    // Fallback logic
    this.updateNextFilterFallback();
  }

  /**
   * Fallback next filter logic when brain unavailable
   */
  updateNextFilterFallback() {
    const { nextFilter, filterDropdownButton, filterDropdownMenu } = this.elements;
    const navSteps = this.getFallbackNavSteps();
    const filteredProducts = this.getFilteredProducts();
    const nextStep = navSteps[this.currentStep];
    
    // Check if we should hide the filter dropdown
    const isLastStep = this.currentStep >= navSteps.length;
    const hasNextStepOptions = this.getNextStepHasOptions();
    
    if (!nextStep || isLastStep || !hasNextStepOptions) {
      nextFilter.classList.add('hidden');
      return;
    }
      nextFilter.classList.remove('hidden');
    
    // Update dropdown with options (use enhanced version for better UX)
    this.populateDropdownOptionsEnhanced(nextStep, filteredProducts);
    this.setupDropdownEvents();
  }

  /**
   * Populate dropdown options
   */
  populateDropdownOptions(step, products) {
    const { filterDropdownButton, filterDropdownMenu } = this.elements;
    
    // Get value counts
    const valueCounts = this.getStepValueCounts(step, products);
    
    // Sort values
    const sortedValues = Object.keys(valueCounts).sort();
    
    // Update button text
    const dropdownText = filterDropdownButton.querySelector('.dropdown-text');
    if (dropdownText) {
      dropdownText.textContent = `Choose ${step.label}...`;
    }
    
    // Clear and populate options
    filterDropdownMenu.innerHTML = '';
    
    sortedValues.forEach(value => {
      const count = valueCounts[value];
      const option = document.createElement('div');
      option.className = 'filter-dropdown-option';
      option.setAttribute('data-value', value);
      option.setAttribute('role', 'option');
      option.setAttribute('tabindex', '0');
      option.innerHTML = `
        <span class="option-text">${value}</span>
        <span class="dropdown-option-count">${count}</span>
      `;
      
      option.addEventListener('click', () => {
        this.selectDropdownOption(step.key, value);
      });
      
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectDropdownOption(step.key, value);
        }
      });
      
      filterDropdownMenu.appendChild(option);
    });
  }

  /**
   * Get curriculum brand descriptions for enhanced user experience
   */
  getCurriculumDescriptions() {
    return {
      'MONARCH': {
        description: 'Online, self-paced learning',
        features: ['Computer-based instruction', 'Progress tracking', 'Multimedia lessons']
      },
      'LIFEPAC': {
        description: 'Mastery-based print worktexts',
        features: ['Self-contained workbooks', 'Individual progression', 'Print-based learning']
      },
      'Horizons': {
        description: 'Spiral learning approach',
        features: ['Continuous review', 'Concept reinforcement', 'Colorful print materials']
      },
      'Resources': {
        description: 'Supplementary materials',
        features: ['Additional support', 'Enrichment activities', 'Flexible implementation']
      }
    };
  }

  /**
   * Enhanced populate dropdown options with curriculum descriptions
   */
  populateDropdownOptionsEnhanced(step, products) {
    const { filterDropdownButton, filterDropdownMenu } = this.elements;
    
    // Get value counts
    const valueCounts = this.getStepValueCounts(step, products);
    
    // Sort values
    const sortedValues = Object.keys(valueCounts).sort();
    
    // Update button text
    const dropdownText = filterDropdownButton.querySelector('.dropdown-text');
    if (dropdownText) {
      dropdownText.textContent = `Choose ${step.label}...`;
    }
    
    // Clear and populate options
    filterDropdownMenu.innerHTML = '';
    
    // Check if this is a curriculum dropdown
    const isCurriculumDropdown = step.key === 'vendor' || step.label.toLowerCase().includes('curriculum');
    const curriculumDescriptions = isCurriculumDropdown ? this.getCurriculumDescriptions() : null;
    
    sortedValues.forEach(value => {
      const count = valueCounts[value];
      const option = document.createElement('div');
      option.className = 'filter-dropdown-option';
      option.setAttribute('data-value', value);
      option.setAttribute('role', 'option');
      option.setAttribute('tabindex', '0');
        // Enhanced curriculum option with description (case-insensitive lookup)
      const curriculumKey = isCurriculumDropdown ? 
        Object.keys(curriculumDescriptions || {}).find(key => 
          key.toLowerCase() === value.toLowerCase()
        ) : null;
        if (isCurriculumDropdown && curriculumKey && curriculumDescriptions[curriculumKey]) {
        const curriculum = curriculumDescriptions[curriculumKey];
        const featuresText = curriculum.features ? curriculum.features.join(', ') : '';
        
        option.innerHTML = `
          <div class="option-content">
            <div class="option-main">
              <span class="option-text">${value}</span>
              <span class="dropdown-option-count">${count}</span>
            </div>
            <div class="option-description">
              ${curriculum.description}
            </div>
            ${featuresText ? `<div class="option-features">${featuresText}</div>` : ''}
          </div>
        `;
        option.classList.add('enhanced-curriculum-option');
      } else {
        // Standard option layout
        option.innerHTML = `
          <span class="option-text">${value}</span>
          <span class="dropdown-option-count">${count}</span>
        `;
      }
      
      option.addEventListener('click', () => {
        this.selectDropdownOption(step.key, value);
      });
      
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectDropdownOption(step.key, value);
        }
      });
      
      filterDropdownMenu.appendChild(option);
    });
  }

  /**
   * Get value counts for a filter step
   */
  getStepValueCounts(step, products) {
    const counts = {};
    
    products.forEach(product => {
      const value = product[step.key];
      if (value && value.trim()) {
        const cleanValue = value.trim();
        counts[cleanValue] = (counts[cleanValue] || 0) + 1;
      }
    });
    
    return counts;
  }  /**
   * Handle dropdown option selection with smart cache management
   */
  selectDropdownOption(filterKey, filterValue) {
    const { finderControls } = this.elements;
    
    // Store the previous scroll width before changes
    const prevScrollWidth = finderControls ? finderControls.scrollWidth : 0;
      // Reset modal close flags when user makes new filter selections
    // This allows results to show for new filtering paths
    this.modalManuallyClosedRecently = false;
    this.modalClosedForFilterState = null;
    
    // Use brain's addFilter method if available for smart cache management
    if (this.smartFilterBrain && this.smartFilterBrain.addFilter) {
      this.currentFilters = this.smartFilterBrain.addFilter(filterKey, filterValue, this.currentFilters);
    } else {
      // Fallback: direct assignment
      this.currentFilters[filterKey] = filterValue;
    }
    
    this.currentStep++;
    this.updateInterface();
    
    // Update scroll arrows and auto-scroll after adding new filter
    setTimeout(() => this.autoScrollToNewContent(prevScrollWidth), 50);
  }

  /**
   * Get filtered products with brain delegation
   */
  getFilteredProducts() {
    // Delegate to brain if available
    if (this.smartFilterBrain && this.smartFilterBrain.getFilteredProducts) {
      return this.smartFilterBrain.getFilteredProducts(this.allProducts, this.currentFilters);
    }

    // Fallback filtering logic
    return this.allProducts.filter(product => {
      return Object.keys(this.currentFilters).every(filterKey => {
        const filterValue = this.currentFilters[filterKey];
        const productValue = product[filterKey];
        return productValue && 
               productValue.toString().toLowerCase().includes(filterValue.toLowerCase());
      });
    });
  }
  /**
   * Check and display results with brain delegation
   */  checkForResults() {
    const { finalResults, finalContent } = this.elements;
    
    // Smart modal prevention: only block if we're at the exact same filter state where modal was closed
    if (this.modalManuallyClosedRecently && this.modalClosedForFilterState) {
      const currentStateKey = JSON.stringify(this.currentFilters);
      const closedStateKey = JSON.stringify(this.modalClosedForFilterState);
        if (currentStateKey === closedStateKey) {
        this.log('🚫 Smart Product Finder: Skipping automatic modal - Same filter state as when user closed modal');
        return;
      } else {
        this.log('🎯 Smart Product Finder: Filter state changed since modal close - Allowing modal to show for new state');
        // Clear the flag since we're in a different state now
        this.modalManuallyClosedRecently = false;
        this.modalClosedForFilterState = null;
      }
    }
    
    // Delegate to brain for smart decisions
    if (this.smartFilterBrain && this.smartFilterBrain.checkForResults) {
      return this.smartFilterBrain.checkForResults({
        finalResults,
        finalContent,
        allProducts: this.allProducts,
        currentFilters: this.currentFilters,
        getFilteredProducts: () => this.getFilteredProducts(),
        buildFilterUrl: () => this.buildFilterUrl(),
        onClose: () => this.handleModalClose()
      });
    }

    // Fallback logic
    this.checkForResultsFallback();
  }/**
   * Fallback result checking logic
   */
  checkForResultsFallback() {
    const filteredProducts = this.getFilteredProducts();
    const { finalResults, finalContent } = this.elements;
    
    if (!finalResults || !finalContent) return;
    
    // Show results if we have a reasonable number (match brain's ≤5 target)
    if (filteredProducts.length > 0 && filteredProducts.length <= 5) {
      finalResults.classList.remove('hidden');
      finalContent.innerHTML = `
        <h4>Found ${filteredProducts.length} products</h4>
        <p>Smart Filter Brain not available - showing basic results</p>
      `;
    } else {
      finalResults.classList.add('hidden');
    }
  }
  /**
   * Handle modal close - enables discovery mode by allowing continued filtering
   */  handleModalClose() {
    this.log('🔄 Smart Product Finder: Modal closed - Discovery mode active', {
      currentFilters: this.currentFilters,
      filterCount: Object.keys(this.currentFilters).length,
      timestamp: new Date().toLocaleTimeString(),
      canContinueFiltering: true
    });

    // Enhanced logging for backtracking operations
    this.log('📊 Modal Close - Current State:', {
      totalProducts: this.allProducts.length,
      currentStep: this.currentStep,
      appliedFilters: Object.keys(this.currentFilters),
      filterHistory: this.smartFilterBrain?.stepHistory || [],
      cacheState: this.smartFilterBrain ? {
        cacheSize: this.smartFilterBrain.cache?.size || 0,
        filterHistoryDepth: this.smartFilterBrain.filterHistory?.length || 0
      } : 'Brain not available'
    });    // Smart modal prevention: capture the exact filter state when modal was closed
    this.modalManuallyClosedRecently = true;    this.modalClosedForFilterState = { ...this.currentFilters }; // Deep copy current state
    
    this.log('🔒 Smart Product Finder: Captured filter state when modal closed:', {
      capturedState: this.modalClosedForFilterState,
      stateHash: JSON.stringify(this.modalClosedForFilterState)
    });
    
    // Reset the flags after 3 seconds to allow normal behavior for new filter selections
    setTimeout(() => {
      this.modalManuallyClosedRecently = false;
      this.modalClosedForFilterState = null;
      this.log('⏰ Smart Product Finder: Auto-modal prevention timeout expired - Normal behavior restored');
    }, 3000);

    // Update the interface to show current filters and enable continued navigation
    this.updateInterface();

    this.log('✅ Discovery mode ready - User can continue filtering from current state');
  }

  /**
   * Check if next step has options - delegates to brain
   */
  getNextStepHasOptions() {
    if (this.smartFilterBrain && this.smartFilterBrain.getNextStepHasOptions) {
      const navSteps = this.smartFilterBrain.getNavigationSteps();
      return this.smartFilterBrain.getNextStepHasOptions(
        this.allProducts, 
        this.currentFilters, 
        this.currentStep, 
        navSteps, 
        () => this.getFilteredProducts()
      );
    }
    
    // Fallback: assume there are always options
    return true;
  }

  /**
   * Build filter URL for collection page
   */
  buildFilterUrl() {
    const params = new URLSearchParams();
    Object.keys(this.currentFilters).forEach(key => {
      params.append(`filter.${key}`, this.currentFilters[key]);
    });
    return params.toString();
  }

  /**
   * Get navigation steps (with brain delegation if available)
   */
  getNavigationSteps() {
    if (this.smartFilterBrain && this.smartFilterBrain.getNavigationSteps) {
      return this.smartFilterBrain.getNavigationSteps();
    }
    return this.getFallbackNavSteps();
  }

  /**
   * Get fallback navigation steps
   */
  getFallbackNavSteps() {
    return [
      { key: 'custom.product_type', label: 'Product Type' },
      { key: 'vendor', label: 'Curriculum' },
      { key: 'custom.grade_level', label: 'Grade Level' },
      { key: 'custom.subject', label: 'Subject' },
      { key: 'custom.course_type', label: 'Course Type' },
      { key: 'custom.learning_style', label: 'Learning Style' }
    ];
  }  /**
   * Remove a filter with smart cache invalidation
   */  
  removeFilter(filterKey) {
    // DON'T reset modal close flag when backtracking - let user choose when to see results
    // Flag should only reset when user makes NEW selections, not when removing filters
    
    // BREADCRUMB BACKTRACKING: Special logic for breadcrumb button clicks
    // Need to handle positioning differently from regular filter removal
    this.removeFilterWithBreadcrumbPositioning(filterKey);
  }

  /**
   * Handle breadcrumb backtracking with proper positioning
   */
  removeFilterWithBreadcrumbPositioning(filterKey) {
    const navSteps = this.getNavigationSteps();
    const removedStepIndex = navSteps.findIndex(step => step.key === filterKey);
    
    if (removedStepIndex === -1) {
      this.log(`Filter key ${filterKey} not found in navigation steps`);
      return;
    }

    // Use brain's removeFilter method if available for smart cache management
    if (this.smartFilterBrain && this.smartFilterBrain.removeFilter) {
      this.currentFilters = this.smartFilterBrain.removeFilter(filterKey, this.currentFilters);
      
      // BREADCRUMB POSITIONING FIX: Set currentStep to the position where we backtracked
      // This ensures next dropdown shows the correct step, not the end of step history
      this.currentStep = removedStepIndex;
      this.log(`Breadcrumb backtrack: set currentStep to ${removedStepIndex} for filter ${filterKey}`);
      
      // Additional cleanup: Remove any subsequent filters that brain didn't handle
      navSteps.slice(removedStepIndex + 1).forEach(step => {
        if (this.currentFilters[step.key]) {
          delete this.currentFilters[step.key];
          this.log(`Cleaned up subsequent filter: ${step.key}`);
        }
      });
      
    } else {
      // Fallback: manual filter removal with subsequent filter clearing
      delete this.currentFilters[filterKey];
      
      // Remove all filters that come after this step in the sequence
      navSteps.slice(removedStepIndex + 1).forEach(step => {
        delete this.currentFilters[step.key];
      });
      
      // Reset current step to the removed filter's step
      this.currentStep = removedStepIndex;
    }
    
    // CRITICAL FIX: Clear skip flag when backtracking to prevent premature results
    // This ensures that navigating backward doesn't trigger the "skip option selected" logic
    if (this.currentFilters.skip_optional_filters) {
      delete this.currentFilters.skip_optional_filters;
    }
    
    // Update the interface to reflect changes
    this.updateInterface();
    
    // If we removed all filters, ensure title is shown
    if (Object.keys(this.currentFilters).length === 0) {
      this.updateTitle();
    }
  }/**
   * Reset all filters with smart cache reset
   */
  resetFilters() {    // Reset modal close flags when filters are reset
    this.modalManuallyClosedRecently = false;
    this.modalClosedForFilterState = null;
    
    // Use brain's resetForPageRefresh method if available for complete state reset
    if (this.smartFilterBrain && this.smartFilterBrain.resetForPageRefresh) {
      this.smartFilterBrain.resetForPageRefresh();
    }
    
    this.currentFilters = {};
    this.currentStep = 0;
    
    // Ensure skip flag is completely cleared on reset
    if (this.currentFilters.skip_optional_filters) {
      delete this.currentFilters.skip_optional_filters;
    }
    
    this.updateInterface();
  }

  /**
   * Update title visibility
   */
  updateTitle() {
    const { finderTitle } = this.elements;
    
    if (finderTitle) {
      const hasFilters = Object.keys(this.currentFilters).length > 0;
      finderTitle.style.display = hasFilters ? 'none' : 'block';
    }
  }
  /**
   * Cleanup dropdown event handlers
   */
  cleanupDropdownEvents() {
    const { filterDropdownButton, finderControls } = this.elements;
    
    if (this.eventHandlers.toggleDropdown) {
      filterDropdownButton?.removeEventListener('click', this.eventHandlers.toggleDropdown);
    }
    if (this.eventHandlers.handleDropdownKeydown) {
      filterDropdownButton?.removeEventListener('keydown', this.eventHandlers.handleDropdownKeydown);
    }
    if (this.eventHandlers.closeDropdownOutside) {
      document.removeEventListener('click', this.eventHandlers.closeDropdownOutside);
    }
    if (this.eventHandlers.closeDropdownOnHorizontalScroll) {
      finderControls?.removeEventListener('scroll', this.eventHandlers.closeDropdownOnHorizontalScroll);
    }
    if (this.eventHandlers.closeDropdownOnWindowScroll) {
      window.removeEventListener('scroll', this.eventHandlers.closeDropdownOnWindowScroll);
    }
  }

  /**
   * Cleanup window event handlers
   */
  cleanupWindowEvents() {
    if (this.eventHandlers.horizontalScroll && this.elements.finderControls) {
      this.elements.finderControls.removeEventListener('scroll', this.eventHandlers.horizontalScroll);
    }
    if (this.eventHandlers.windowScroll) {
      window.removeEventListener('scroll', this.eventHandlers.windowScroll);
    }
  }

  /**
   * Cleanup resources and event listeners
   */
  cleanup() {
    // Remove global functions
    delete window[`removeFilter_${this.sectionId}`];
    delete window[`resetFilters_${this.sectionId}`];

    // Cleanup event handlers
    this.cleanupDropdownEvents();
    this.cleanupWindowEvents();
    
    // Remove scroll handlers
    if (this.eventHandlers.scroll && this.elements.finderControls) {
      this.elements.finderControls.removeEventListener('scroll', this.eventHandlers.scroll);
    }
    if (this.eventHandlers.resize) {
      window.removeEventListener('resize', this.eventHandlers.resize);
    }
    
    // Disconnect ResizeObserver if it exists
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clear brain cache if available
    if (this.smartFilterBrain && this.smartFilterBrain.clearCache) {
      this.smartFilterBrain.clearCache();
    }
  }

  /**
   * Auto-scroll to show newly added content if it's off-screen
   * @param {number} prevScrollWidth - The scroll width before content was added
   */
  autoScrollToNewContent(prevScrollWidth) {
    const { finderControls } = this.elements;
    
    if (!finderControls) return;
    
    // First update the arrows to reflect the current state
    if (this.eventHandlers && this.eventHandlers.scroll) {
      this.eventHandlers.scroll();
    }
    
    // Check if content width increased (indicating new items were added)
    if (finderControls.scrollWidth > prevScrollWidth) {
      // Calculate if we need to scroll to show new content
      const newContentWidth = finderControls.scrollWidth - prevScrollWidth;
      const visibleRight = finderControls.scrollLeft + finderControls.clientWidth;
      
      // If the visible area doesn't include the new content, scroll to show it
      if (finderControls.scrollWidth > visibleRight) {
        const scrollNeeded = Math.min(
          newContentWidth + 20, // Scroll to see the new content plus some padding
          finderControls.scrollWidth - finderControls.clientWidth // Don't scroll beyond the maximum
        );
        
        // Smooth scroll to show the new content
        finderControls.scrollBy({
          left: scrollNeeded,
          behavior: 'smooth'
        });
      }
    }
  }
}

// Export the class for use
window.SmartProductFinderUI = SmartProductFinderUI;
